/**
 * FDA Orange Book "type" (listing status) labels — RX/OTC/DISCN.
 * Source: FDA Orange Book Data Files — Products.txt field description
 * https://www.fda.gov/drugs/drug-approvals-and-databases/orange-book-data-files
 *
 * Mirrors the applTypeLabels.ts/teCodeLabels.ts pattern used for
 * Application Type and TE Code: one shared lookup consumed by every
 * surface that renders `type` as its own field (human direction,
 * 2026-09-28 — a bare "RX"/"OTC"/"DISCN" carries no explanation anywhere
 * it's shown standalone, same gap those two fields had before their own
 * 2026-09-23 follow-up fix).
 */
import { ORANGE_BOOK_DATA_FILES_URL } from './applTypeLabels';

export { ORANGE_BOOK_DATA_FILES_URL };

export interface ObTypeInfo {
  shortName: string;
  fullDescription: string;
}

export const OB_TYPE_LABELS: Record<string, ObTypeInfo> = {
  RX: {
    shortName: 'Prescription',
    fullDescription: 'Prescription drug products are coded "RX".',
  },
  OTC: {
    shortName: 'Over-the-Counter',
    fullDescription: 'Over-the-counter drug products are coded "OTC".',
  },
  DISCN: {
    shortName: 'Discontinued',
    fullDescription:
      'Discontinued listings are coded "DISCN" — the FDA has discontinued this product or withdrawn its approval.',
  },
};

/** Resolves a raw `type` code to its label info. Returns `undefined` for anything not RX/OTC/DISCN. */
export function getObTypeInfo(code: string): ObTypeInfo | undefined {
  const trimmed = code.trim().toUpperCase();
  if (!trimmed) return undefined;
  return OB_TYPE_LABELS[trimmed];
}

/** `"[code] - [Short name]"`, or the raw code if unrecognized. */
export function formatObType(code: string): string {
  const info = getObTypeInfo(code);
  return info ? `${code} - ${info.shortName}` : code;
}
